import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { Link } from "react-router-dom";

export default function AboutPage() {
  return (
    <>
      <Seo
        title="About DuroTech28 | Low Voltage Specialist in DFW"
        description="The story, mission and promise behind DuroTech28 — a Dallas–Fort Worth technology installation company bridging physical and digital infrastructure."
        canonical="https://durotech28.me/about"
      />
      <Nav />
      <main className="max-w-4xl mx-auto px-6 py-16" data-testid="about-page">
        <span className="text-xs uppercase tracking-widest text-[#d4af37] font-mono">About DuroTech28</span>
        <h1 className="font-display text-4xl sm:text-6xl uppercase tracking-tight text-white mt-3 leading-[1.02]">
          Built on Faith.<br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d4af37] via-[#fff4cc] to-[#c0c0c0]">Driven by Purpose.</span><br />
          Committed to Excellence.
        </h1>

        <blockquote className="my-12 rounded-2xl p-8 bg-[#121216] gold-border text-center">
          <p className="text-lg sm:text-xl text-zinc-200 italic leading-relaxed">
            "Whatsoever thy hand findeth to do, do it with thy might."
          </p>
          <cite className="block mt-4 text-xs font-mono uppercase tracking-widest text-[#d4af37] not-italic">
            — Ecclesiastes 9:10 (KJV)
          </cite>
        </blockquote>

        <section className="mb-12" data-testid="about-foundation">
          <h2 className="font-display text-2xl uppercase text-[#d4af37] mb-4">What We Do</h2>
          <div className="space-y-4 text-sm text-zinc-300 leading-relaxed mb-10">
            <p>
              From TV mounting and smart home technology to enterprise networking, Local AI, and solar
              panel installation, DuroTech28 delivers practical technology solutions for homes and businesses.
            </p>
            <p>
              Our solar installation services combine professional panel mounting with coordination through
              licensed electrical partners, helping customers pursue reliable renewable energy solutions.
            </p>
          </div>
          <h2 className="font-display text-2xl uppercase text-[#d4af37] mb-4">Our Foundation</h2>
          <div className="space-y-4 text-sm text-zinc-300 leading-relaxed">
            <p>At DuroTech28, this scripture is more than a verse. It is the foundation of everything we do.</p>
            <p>We believe that whatever work is placed in our hands deserves our very best.</p>
            <p>
              Whether we're mounting a television, installing a security system, building a commercial network,
              or deploying critical data center infrastructure, our commitment remains the same:
            </p>
            <p className="font-display text-lg uppercase tracking-wide text-white">
              Do it right. Do it with purpose. Do it with excellence.
            </p>
          </div>
        </section>

        <section className="mb-12" data-testid="about-more-than">
          <h2 className="font-display text-2xl uppercase text-[#d4af37] mb-4">More Than a Business</h2>
          <div className="space-y-4 text-sm text-zinc-300 leading-relaxed">
            <p>DuroTech28 was built on a genuine passion for technology, problem-solving, and craftsmanship.</p>
            <p>We understand that businesses must generate revenue to survive and grow. But we also believe that when you truly love what you do, your work becomes about something greater than money.</p>
            <ul className="space-y-2 font-mono text-xs text-zinc-300">
              <li className="flex items-center gap-2"><span className="text-[#d4af37]">▶</span> It becomes about helping people.</li>
              <li className="flex items-center gap-2"><span className="text-[#d4af37]">▶</span> It becomes about solving problems.</li>
              <li className="flex items-center gap-2"><span className="text-[#d4af37]">▶</span> It becomes about building relationships.</li>
              <li className="flex items-center gap-2"><span className="text-[#d4af37]">▶</span> It becomes about earning trust.</li>
            </ul>
            <p>And most importantly, it becomes about taking pride in the work you leave behind.</p>
            <p className="text-white font-medium">Every customer matters. Every project matters. No job is too small to deserve our best effort.</p>
          </div>
        </section>

        <div className="grid sm:grid-cols-2 gap-6 mb-12">
          <section className="rounded-2xl p-7 bg-[#121216] border border-zinc-800" data-testid="about-mission">
            <h2 className="font-display text-xl uppercase text-[#d4af37] mb-3">Our Mission</h2>
            <p className="text-xs text-zinc-300 leading-relaxed">
              To connect homes, businesses, and communities through reliable technology, exceptional
              workmanship, and customer-first service. From residential installations to commercial
              networking and data center infrastructure, DuroTech28 strives to deliver professional
              solutions built around quality, reliability, and innovation.
            </p>
          </section>
          <section className="rounded-2xl p-7 bg-[#121216] border border-zinc-800" data-testid="about-promise">
            <h2 className="font-display text-xl uppercase text-[#d4af37] mb-3">Our Promise</h2>
            <p className="text-xs text-zinc-300 leading-relaxed">
              We believe our reputation is built one customer and one project at a time. We are committed
              to integrity, professionalism, accountability, and excellence. We're not simply installing
              equipment — we're building trust. We're building relationships. We're building a legacy.
            </p>
          </section>
        </div>

        <section className="mb-12 rounded-2xl p-8 bg-gradient-to-b from-[#14141a] to-[#0d0d10] border border-[#d4af37]/30" data-testid="about-vision">
          <h2 className="font-display text-2xl uppercase text-white mb-1">Our Vision — Building the Future</h2>
          <p className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37] mb-5">Future goals — not services or facilities we currently own or operate</p>
          <div className="space-y-4 text-sm text-zinc-300 leading-relaxed">
            <p>DuroTech28's vision extends far beyond traditional technology installation. Our long-term goal is to develop, own, and operate our own data center infrastructure.</p>
            <p>
              We envision building secure, reliable technology infrastructure that supports businesses,
              entrepreneurs, developers, and organizations. This future infrastructure will also be designed
              to support our affiliated company, Duro Capital, and its developing technology and financial platforms.
            </p>
            <p>
              At DuroTech28, we envision a future where technology does more than connect devices — it creates
              intelligent environments. From homes that respond naturally to the people living in them, to
              businesses that automate repetitive workflows, to enterprise systems powered by private AI
              infrastructure, our vision is to make intelligent technology accessible at every level. Our
              long-term ambition is to develop and operate data center infrastructure that supports AI
              computing, hosting, and innovation for Duro Capital and other organizations.
            </p>
          </div>
          <ul className="grid sm:grid-cols-2 gap-2.5 mt-6 text-xs font-mono text-zinc-300">
            {[
              "Data center ownership and operations",
              "Enterprise hosting and cloud infrastructure",
              "Artificial intelligence infrastructure",
              "Secure data storage and processing",
              "Enterprise networking",
              "Business technology solutions",
              "Automation and digital innovation",
            ].map((v) => (
              <li key={v} className="flex items-center gap-2"><span className="text-[#d4af37]">▶</span> {v}</li>
            ))}
          </ul>
          <p className="text-xs text-zinc-400 leading-relaxed mt-6">
            We recognize that achieving this vision requires time, dedication, partnerships, and continuous growth.
            Every installation we complete today contributes to the foundation we're building for tomorrow.
          </p>
        </section>

        <blockquote className="text-center py-8">
          <p className="text-lg sm:text-xl text-zinc-200 italic leading-relaxed">
            "Our hands build the solutions. Our faith guides the purpose.<br className="hidden sm:block" />
            Our commitment to excellence defines the legacy."
          </p>
          <p className="mt-6 font-display text-2xl uppercase tracking-wider text-white">DuroTech28</p>
          <p className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37] mt-1">Low Voltage Specialist · One System. Complete Solutions.</p>
        </blockquote>

        <div className="text-center mt-6">
          <Link to="/services" data-testid="about-explore-btn"
            className="inline-block px-8 py-4 rounded bg-gradient-to-r from-[#d4af37] to-[#e6ca65] text-black font-bold uppercase tracking-wider text-sm hover:opacity-95 transition-opacity shadow-lg shadow-[#d4af37]/25">
            Explore Services →
          </Link>
        </div>
      </main>
      <Footer />
    </>
  );
}
